import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiClient from '../config/api'
import { useRouter } from 'vue-router'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const isAuthenticated = ref(false)
  const router = useRouter()

  const persistAuthenticatedUser = (database, userData, fallbackUsername = '') => {
    const normalizedUser = {
      ...userData,
      username: userData.username || fallbackUsername,
      is_admin: userData.is_admin === true
    }

    user.value = normalizedUser
    isAuthenticated.value = true

    localStorage.setItem('uid', normalizedUser.uid)
    localStorage.setItem('user_context', JSON.stringify(normalizedUser.user_context))
    localStorage.setItem('username', normalizedUser.username)
    localStorage.setItem('name', normalizedUser.name)
    localStorage.setItem('company_id', normalizedUser.company_id)
    localStorage.setItem('partner_id', normalizedUser.partner_id)
    localStorage.setItem('database', database)
    localStorage.setItem('isAdmin', String(normalizedUser.is_admin))

    return normalizedUser
  }

  // Listen for storage changes
  window.addEventListener('storage', (e) => {
    if (e.key === 'uid' && !e.newValue) {
      handleLogout()
    }
  })

  const login = async (database, email, password) => {
    try {
      const payload = {
        jsonrpc: '2.0',
        id: new Date().getTime(),
        params: {
          db: database,
          login: email,
          password: password
        }
      }
  
      console.log('📡 Login attempt:', {
        database,
        email,
        timestamp: new Date().toISOString()
      })
  
      const response = await apiClient.post('/web/session/authenticate', payload)

      const sessionId = response.headers['x-openerp-session-id']
      if (sessionId) {
        localStorage.setItem('sessionId', sessionId)
      }

      if (response.data.error) {
        throw new Error(response.data.error.data?.message || 'Authentication failed')
      }

      return persistAuthenticatedUser(database, response.data.result, email)
    } catch (error) {
      console.error('❌ Login failed:', error)
      throw error
    }
  }

  const switchBranch = async (database) => {
    if (!user.value?.is_admin) {
      throw new Error('Hanya administrator yang dapat mengganti cabang')
    }

    if (database === localStorage.getItem('database')) {
      throw new Error('Cabang tujuan sama dengan cabang aktif')
    }

    let response
    try {
      response = await apiClient.post('/web/session/switch_branch', {
        jsonrpc: '2.0',
        method: 'call',
        id: new Date().getTime(),
        params: { target_db: database }
      })
    } catch (error) {
      console.error('Branch switch request failed:', error)
      const status = error.response?.status

      if (status === 404) {
        throw new Error('Fitur ganti cabang belum aktif di server. Deploy controller dan restart service Odoo lalu coba lagi.')
      }

      if (status >= 500) {
        throw new Error('Server gagal menyiapkan sesi cabang. Periksa log Odoo lalu coba lagi.')
      }

      throw new Error('Server tidak dapat dihubungi. Periksa koneksi lalu coba lagi.')
    }

    if (response.data.error) {
      const sessionExpired = response.data.error.code === 100
      throw new Error(sessionExpired
        ? 'Sesi Anda telah berakhir. Silakan login kembali.'
        : response.data.error.data?.message || 'Sesi cabang gagal dibuat')
    }

    const result = response.data.result
    if (result?.status !== 'success' || !result.data) {
      throw new Error(result?.message || 'Cabang belum dapat dibuka')
    }

    // Autoswitch memakai cookie HttpOnly baru dari server. Jangan kirim lagi
    // session ID lama yang mungkin pernah disimpan oleh alur login sebelumnya.
    localStorage.removeItem('sessionId')
    return persistAuthenticatedUser(database, result.data, user.value.username)
  }

  const logout = async () => {
    try {
      await apiClient.post('/web/session/destroy', {})
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      user.value = null
      isAuthenticated.value = false
      localStorage.clear()
      router.push('/login')
    }
  }

  const checkAuth = () => {
    const uid = localStorage.getItem('uid')
    const username = localStorage.getItem('username')
    
    if (uid && username) {
      isAuthenticated.value = true
      user.value = {
        uid,
        username,
        name: localStorage.getItem('name'),
        company_id: localStorage.getItem('company_id'),
        partner_id: localStorage.getItem('partner_id'),
        is_admin: localStorage.getItem('isAdmin') === 'true'
      }
    } else {
      logout()
    }
  }

  return {
    user,
    isAuthenticated,
    login,
    switchBranch,
    logout,
    checkAuth
  }
})
